import React, { useEffect, useState, useCallback } from "react";
import { useCreateData } from "hooks/useApiHooks";
import FormGroup from "./FormGroup";
import { useAgent } from "contexts/v1/masterData/AgentContext";
import { useTrip } from "contexts/v1/masterData/TripContext";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import Swal from "sweetalert2";
import { sendBookingCreatedEvent } from "services/websocket/websocketUtils";

const QuickCreatePage = ({ onSuccess, onError }) => {
  const { agents, fetchOptions: fetchAgentOptions } = useAgent();
  const { trips } = useTrip();

  const API_URL = "/reservation/quick";
  const { createData: createBooking } = useCreateData(API_URL, "bookings");

  const [formData, setFormData] = useState({
    reservation_date: new Date().toISOString().split("T")[0],
    trip_id: null,
    agent_id: null,
    guest_nationality: null,
    driver: null,
  });

  // ✅ Fungsi yang menangani perubahan semua input (React-Select & input biasa)
  const handleChange = (selected, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selected ? selected.value : null, // ✅ Pastikan tidak membaca `.value` dari `null`
    }));
  };

  // // 🔹 Ambil semua data agent di awal
  // useEffect(() => {
  //   agents.refetch();
  // }, []);

  // 🔹 Mapping data trip ke dalam Select Options
  const tripOptions =
    trips?.data?.map((trip) => ({
      value: trip.id,
      label: `${trip.name} (${trip.start_time} - ${trip.end_time}) - Sisa ${trip.remaining}`,
    })) || [];

  // 🔹 Menentukan trip yang sudah dipilih
  const selectedTrip =
    tripOptions.find((trip) => trip.value === formData.trip_id) || null;

  // 🔹 Menentukan agent yang sudah dipilih
  const selectedAgent =
    formData.agent_id &&
    agents?.data.find((agent) => agent.id === formData.agent_id)
      ? {
          value: formData.agent_id,
          label: agents?.data.find((agent) => agent.id === formData.agent_id)
            .name,
        }
      : null;

  // ✅ Fungsi untuk cek ketersediaan sebelum booking
  const checkAvailability = () => {
    const selectedTrip = trips?.data.find(
      (trip) => trip.id === formData.trip_id
    );
    if (!selectedTrip) {
      Swal.fire("Error", "Trip tidak ditemukan!", "error");
      return false;
    }

    if (selectedTrip.remaining < 10) {
      Swal.fire(
        "Fully Booked",
        `${selectedTrip.name} hanya tersisa ${selectedTrip.remaining} slot.`,
        "error"
      );
      return false;
    }

    return true;
  };

  // ✅ Fungsi untuk mengirim booking setelah cek availability
  const handleBulkSubmit = async (e) => {
    e.preventDefault();

    // if (!formData.trip_id || !formData.agent_id) {
    //   Swal.fire("Warning", "Pilih Trip dan Agent terlebih dahulu!", "warning");
    //   return;
    // }

    if (!checkAvailability()) return;

    const booking = {
      reservation_date: formData.reservation_date,
      trip_id: formData.trip_id,
      agent_id: formData.agent_id,
      driver: formData.driver,
      guest_nationality: formData.guest_nationality
    };

    try {
      const response = await createBooking(booking);
      if (!response?.success) {
        throw response?.errorResponse || new Error("Failed to create booking");
      }

      onSuccess?.(response?.data);
      sendBookingCreatedEvent(response?.data || booking).catch((err) =>
        console.warn(
          "[BOOKING_V3_WEBSOCKET] Failed to send quick booking event:",
          err
        )
      );
      Swal.fire("Success!", "Booking created!", "success");
    } catch (error) {
      console.error("❌ Gagal membuat booking:", error);
      onError?.(error);
      Swal.fire("Error!", "Failed to create booking!", "error");
    }
  };

  return (
    <form onSubmit={handleBulkSubmit} className="d-flex flex-column">
      <div className="action-sheet-content flex-grow-1">
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title">Quick Booking</h5>
          </div>
          <div className="card-body">
            {/* Select Booking Date */}
            <FormGroup
              id="reservation_date"
              label="Booking Date"
              type="date"
              value={formData.reservation_date}
              onChange={(e) => handleChange(e.target, "reservation_date")}
            />

            {/* Select Trip */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-4 col-form-label d-flex justify-content-end">
                Select Trip :
              </label>
              <div className="col-sm-8">
                <Select
                  options={tripOptions}
                  value={selectedTrip}
                  onChange={(selected) => handleChange(selected, "trip_id")}
                  isClearable
                  placeholder="Select a Trip"
                />
              </div>
            </div>

            {/* Select Agent (Async Searchable) */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-4 col-form-label d-flex justify-content-end">
                Select Agent :
              </label>
              <div className="col-sm-8">
                <AsyncSelect
                  cacheOptions
                  loadOptions={fetchAgentOptions} // ✅ Gunakan fetchOptions agar bisa search
                  defaultOptions={agents?.data.map((agent) => ({
                    value: agent.id,
                    label: agent.name,
                  }))}
                  value={selectedAgent}
                  onChange={(selected) => handleChange(selected, "agent_id")}
                  isClearable
                  placeholder="Search Agent..."
                />
              </div>
            </div>
            {/* driver */}
            <div className="row mb-3 align-items-center">
            <label className="col-sm-4 col-form-label d-flex justify-content-end">
                Driver :
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={formData?.driver || ""}
                  placeholder="Driver Name"
                  onChange={(e) => handleChange(e.target, "driver")}
                />
              </div>
            </div>

            {/* hotel */}
            <div className="row mb-3 align-items-center">
            <label className="col-sm-4 col-form-label d-flex justify-content-end">
                Hotel :
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={formData?.guest_nationality || ""}
                  placeholder="Hotal Name"
                  onChange={(e) => handleChange(e.target, "guest_nationality")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group mt-auto d-grid">
        <button type="submit" className="btn btn-primary btn-block">
          Create Booking
        </button>
      </div>
    </form>
  );
};

export default QuickCreatePage;
